import { z } from 'zod';
import {
  A2_DISEASE_OTHER_ID,
  INCIDENT_DATE_TYPE_ID,
  R2_SYMPTOM_JOINT_OTHER_ID,
  R2_SYMPTOM_JOINT_OTHER_REQUIRED_MESSAGE,
  WALKING_CONDITION_ID,
  WALKING_CONDITION_REASON_OTHER_ID,
} from '@/constants/consentForm';

const zArrayDefault = <T extends z.ZodTypeAny>(item: T) =>
  z.preprocess(v => (v == null ? [] : v), z.array(item)).default([]);

/**
 * Consent Form Base Schema (Fields common to A2 and R2)
 */
export const baseConsentSchema = z.object({
  serial_number: z.number().optional(),
  injury_name: z
    .string()
    .max(255, '傷病名①は255文字以内で入力してください。')
    .optional()
    .nullable(),
  same_content: z.boolean().default(false),
  incident_cause: z
    .string({ required_error: '発病・負傷原因及び経過は必須です。' })
    .trim()
    .min(1, '発病・負傷原因及び経過は必須です。')
    .max(100, '発病・負傷原因及び経過は100文字以内で入力してください。'),
  incident_date_type_id: z.number().default(1),
  incident_date: z.string().optional().nullable(),
  incident_type_id: z.number().default(1),
  examine_date: z
    .string({ required_error: '医療機関での診察年月日は必須です。' })
    .min(1, '医療機関での診察年月日は必須です。'),
  first_treatment_date: z
    .string({ required_error: '初療年月日は必須です。' })
    .min(1, '初療年月日は必須です。'),
  /** A2: checkbox values. R2: keep [] (use disease_name_r2). */
  disease_name: zArrayDefault(z.string()),
  /** R2: 傷病名② free text */
  disease_name_r2: z
    .string()
    .max(255, '傷病名②は255文字以内で入力してください。')
    .optional()
    .nullable()
    .default(''),
  disease_name_other: z
    .string()
    .max(255, 'その他の詳細は255文字以内で入力してください。')
    .optional()
    .nullable(),
  walking_condition_id: z.number().default(1),
  is_not_transport_cost: z.boolean().default(false),
  walking_condition_reason_type_id: zArrayDefault(z.string()),
  walking_condition_reason_other: z.string().max(255).optional().nullable(),
  walking_condition_reason_level: z.string().max(255).optional().nullable(),
  hospital_id: z.number().optional().nullable(),
  hospital_prefecture_id: z.number().optional().nullable(),
  hospital_name: z
    .string({ required_error: '医療機関名は必須です。' })
    .trim()
    .min(1, '医療機関名は必須です。')
    .max(255),
  doctor_id: z.number().optional().nullable(),
  doctor_name: z
    .string({ required_error: '医師名は必須です。' })
    .trim()
    .min(1, '医師名は必須です。')
    .max(255),
  // Optional fields: validate only when user inputs something.
  hospital_postal_code1: z.preprocess(
    v => (v == null || v === '' ? undefined : v),
    z.string().length(3, '郵便番号1は3桁にしてください。').optional()
  ),
  hospital_postal_code2: z.preprocess(
    v => (v == null || v === '' ? undefined : v),
    z.string().length(4, '郵便番号2は4桁にしてください。').optional()
  ),
  hospital_address: z
    .string({ required_error: '医療機関住所は必須です。' })
    .trim()
    .min(1, '医療機関住所は必須です。')
    .max(255),
  hospital_telephone: z
    .string()
    .max(13, '電話番号の書式が正しくありません。')
    .regex(/^[\d-]*$/, '電話番号の書式が正しくありません。')
    .refine(val => {
      if (!val) return true;
      const digits = val.replace(/\D/g, '').length;
      return digits >= 7 && digits <= 13;
    }, '電話番号の書式が正しくありません。')
    .optional()
    .nullable(),
  consent_date: z.string({ required_error: '同意日は必須です。' }).min(1, '同意日は必須です。'),
  treatment_term_from: z
    .string({ required_error: '要加療期間開始日は必須です。' })
    .min(1, '要加療期間開始日は必須です。'),
  treatment_term_to: z
    .string({ required_error: '要加療期間終了日は必須です。' })
    .min(1, '要加療期間終了日は必須です。'),
  is_treatment_complete: z.boolean().default(false),
  remarks: z.string().max(255).optional().nullable(),
  is_continuation: z.boolean().default(false),
  is_special: z.boolean().default(false),
  is_ahaki_both_treatment: z.boolean().default(false),
  /** HIT-7357: set at submit when the user confirms the あはき counterpart auto-update; not a form field. */
  ahaki_sync_counterparts: z.boolean().optional(),
  treatment_content: zArrayDefault(z.string()),
  print_cols: zArrayDefault(z.string()),
  non_print_cols: zArrayDefault(z.string()),

  /** R2 — serialized to comma-separated strings on submit */
  symptom_muscle: zArrayDefault(z.string()),
  symptom_joint: zArrayDefault(z.string()),
  symptom_joint_other: z.string().max(255).optional().nullable(),
  symptom_other: z.string().max(255).optional().nullable(),
  is_symptom_print: z.boolean().default(true),
  massage_parts: zArrayDefault(z.string()),
  orthomorphia_parts: zArrayDefault(z.string()),
  hot_treatment_type_id: z.number().optional().nullable(),
});

const sharedRefine = (data: any, ctx: z.RefinementCtx) => {
  if (data.treatment_term_from && data.treatment_term_to) {
    if (new Date(data.treatment_term_from) > new Date(data.treatment_term_to)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '要加療期間開始日は要加療期間終了日以前の日付にしてください。',
        path: ['treatment_term_from'],
      });
    }
  }
};

/**
 * A2 Specific Schema (はり・きゅう)
 */
export const a2ConsentSchema = baseConsentSchema.superRefine((data, ctx) => {
  sharedRefine(data, ctx);

  if (!data.injury_name || data.injury_name.trim() === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '傷病名②は必須です。',
      path: ['injury_name'],
    });
  }

  const treatmentContent = data.treatment_content || [];
  if (!treatmentContent.includes('はり') && !treatmentContent.includes('きゅう')) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'はり、きゅうのどちらかにチェックを入れてください。',
      path: ['treatment_content'],
    });
  }

  const dn = data.disease_name || [];
  if (dn.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '傷病名のいずれかにチェックを入れてください。',
      path: ['disease_name'],
    });
  }

  if (
    dn.includes(A2_DISEASE_OTHER_ID) &&
    (!data.disease_name_other || data.disease_name_other.trim() === '')
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'その他にチェックを入れる場合はその詳細も記入してください。',
      path: ['disease_name_other'],
    });
  }

  if (
    data.walking_condition_id === WALKING_CONDITION_ID.REQUIRED &&
    (!data.walking_condition_reason_type_id || data.walking_condition_reason_type_id.length === 0)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '往療が必要な場合、その理由を選択して下さい。',
      path: ['walking_condition_reason_type_id'],
    });
  }

  if (
    data.walking_condition_id === WALKING_CONDITION_ID.REQUIRED &&
    data.walking_condition_reason_type_id?.includes(WALKING_CONDITION_REASON_OTHER_ID) &&
    (!data.walking_condition_reason_other || data.walking_condition_reason_other.trim() === '')
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'その他にチェックを入れる場合はその詳細も記入してください。',
      path: ['walking_condition_reason_other'],
    });
  }

  if (
    data.incident_date_type_id !== INCIDENT_DATE_TYPE_ID.UNKNOWN &&
    (!data.incident_date || data.incident_date.trim() === '')
  ) {
    const errorMsg =
      data.incident_date_type_id === INCIDENT_DATE_TYPE_ID.DATE
        ? '年月日を指定してください。'
        : data.incident_date_type_id === INCIDENT_DATE_TYPE_ID.MONTH
          ? '年月を指定してください。'
          : '年を指定してください。';
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: errorMsg,
      path: ['incident_date'],
    });
  }
});

const R2_SYMPTOM_MSG =
  '症状の「筋麻痺・筋萎縮」「関節拘縮」「その他」のいずれかを入力してください。';
const R2_MASSAGE_MSG = '【マッサージ】または【変形徒手矯正】の部位をチェックしてください。';

/**
 * R2 Specific Schema (あん摩マッサージ)
 */
export const r2ConsentSchema = baseConsentSchema.superRefine((data, ctx) => {
  sharedRefine(data, ctx);

  // HIT-14780: on the R2 form 傷病名① is disease_name_r2 and 傷病名② is injury_name
  // (DiseaseNameFields), so each message must name the label it is shown under.
  if (!data.injury_name || data.injury_name.trim() === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '傷病名②は必須です。',
      path: ['injury_name'],
    });
  }

  const d2 = (data.disease_name_r2 ?? '').trim();
  if (!d2) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '傷病名①は必須です。',
      path: ['disease_name_r2'],
    });
  }

  const sm = data.symptom_muscle ?? [];
  const sj = data.symptom_joint ?? [];
  const hasSymptom = sm.length > 0 || sj.length > 0;
  if (!hasSymptom) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: R2_SYMPTOM_MSG,
      path: ['symptom_muscle'],
    });
  }

  if (sj.includes(R2_SYMPTOM_JOINT_OTHER_ID) && !(data.symptom_joint_other ?? '').trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: R2_SYMPTOM_JOINT_OTHER_REQUIRED_MESSAGE,
      path: ['symptom_joint_other'],
    });
  }

  const mp = data.massage_parts ?? [];
  const op = data.orthomorphia_parts ?? [];
  if (mp.length === 0 && op.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: R2_MASSAGE_MSG,
      path: ['massage_parts'],
    });
  }

  const overlap = mp.filter(x => op.includes(x));
  if (overlap.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'マッサージと変形徒手矯正に同じ部位を選択することはできません。',
      path: ['orthomorphia_parts'],
    });
  }


  if (
    data.walking_condition_id === WALKING_CONDITION_ID.REQUIRED &&
    (!data.walking_condition_reason_type_id || data.walking_condition_reason_type_id.length === 0)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '往療が必要な場合、その理由を選択して下さい。',
      path: ['walking_condition_reason_type_id'],
    });
  }

  if (
    data.walking_condition_id === WALKING_CONDITION_ID.REQUIRED &&
    data.walking_condition_reason_type_id?.includes(WALKING_CONDITION_REASON_OTHER_ID) &&
    (!data.walking_condition_reason_other || data.walking_condition_reason_other.trim() === '')
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'その他にチェックを入れる場合はその詳細も記入してください。',
      path: ['walking_condition_reason_other'],
    });
  }

  if (
    data.incident_date_type_id !== INCIDENT_DATE_TYPE_ID.UNKNOWN &&
    (!data.incident_date || data.incident_date.trim() === '')
  ) {
    const errorMsg =
      data.incident_date_type_id === INCIDENT_DATE_TYPE_ID.DATE
        ? '年月日を指定してください。'
        : data.incident_date_type_id === INCIDENT_DATE_TYPE_ID.MONTH
          ? '年月を指定してください。'
          : '年を指定してください。';
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: errorMsg,
      path: ['incident_date'],
    });
  }
});

export type ConsentFormData = z.infer<typeof baseConsentSchema>;
